import { useState } from "react";
import { Bot, Edit, Trash, UserPlus } from "lucide-react";
import get from "lodash/get";
import { DataGrid } from "@mui/x-data-grid";
import {
  Typography,
  Grid,
  IconButton,
  Paper,
  Box,
  Stack,
  Button,
  Fab,
} from "@mui/material";
import { useUsers } from "../hooks/useUsers";
import { ACTIVE_COLOR, DEFAULT_COLOR, YELLOW_COLOR } from "@/constants/color";
import { UserDrawer } from "../components/forms/User";
import { User } from "../types/user";
import deleteOne from "@/services/users/delete";
import { BOT_URL } from "@/constants/bot";

export default function Page() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { data, isLoading, refetch } = useUsers();

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{ fontWeight: "bold", color: DEFAULT_COLOR, mt: 2 }}
        >
          Usuarios
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }} textAlign="end" sx={{ p: 2 }}>
        <Button
          variant="outlined"
          startIcon={<UserPlus />}
          sx={{
            borderRadius: 2,
            borderColor: DEFAULT_COLOR,
            color: DEFAULT_COLOR,
            boxShadow: 1,
          }}
          onClick={() => setIsDrawerOpen(true)}
        >
          Agregar Usuario
        </Button>
      </Grid>

      <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
        <Paper elevation={3} sx={{ p: 2, overflow: "auto" }}>
          <DataGrid
            loading={isLoading}
            sx={{
              "& .MuiDataGrid-filler": {
                backgroundColor: DEFAULT_COLOR,
                color: "#fff",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: DEFAULT_COLOR,
                color: "#fff",
              },
            }}
            pagination
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            rows={data}
            rowSelection={false}
            columns={[
              { headerName: "Nombre", field: "name", width: 250 },
              { headerName: "Apellido", field: "lastName", width: 250 },
              {
                headerName: "Correo Electrónico",
                field: "email",
                width: 300,
              },
              {
                headerName: "Número Telefónico",
                field: "phone",
                width: 250,
              },
              {
                headerName: "Coordinación",
                field: "cordinations",
                width: 250,
                valueGetter: (_, row) => {
                  return get(row, "cordinations", [])
                    .map((c) => `${c.name} ${c.lastName}`)
                    .join(" | ");
                },
              },
              {
                headerName: "Desarrollo(s)",
                field: "campaign",
                width: 250,
                valueGetter: (_, row) => {
                  return get(row, "campaigns", [])
                    .map((p: { name: string }) => p.name)
                    .join(", ");
                },
              },
              {
                headerName: "Rol",
                field: "role.name",
                width: 300,
                renderCell(params) {
                  return (
                    <Box
                      sx={{
                        border: 2,
                        borderColor: YELLOW_COLOR,
                        color: YELLOW_COLOR,
                        p: 1,
                        mt: 0.5,
                        borderRadius: 3,
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body1">
                        {get(params.row, "role.name", "")}
                      </Typography>
                    </Box>
                  );
                },
                valueGetter: (_, row) => {
                  return get(row, "role.name", "");
                },
              },
              {
                headerName: "Acciones",
                field: "actions",
                width: 200,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      aria-label="editar"
                      sx={{ color: ACTIVE_COLOR }}
                      onClick={() => {
                        setSelectedUser(params.row);
                        setIsDrawerOpen(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      aria-label="eliminar"
                      sx={{ color: "red" }}
                      onClick={async () => {
                        await deleteOne(params.row._id);
                        refetch();
                      }}
                    >
                      <Trash />
                    </IconButton>
                  </Stack>
                ),
              },
            ]}
            getRowId={(row) => row._id}
          />
        </Paper>
      </Grid>
      <Fab
        aria-label="bot"
        sx={{
          backgroundColor: DEFAULT_COLOR,
          color: "white",
          position: "fixed",
          bottom: { xs: 80, sm: 80 },
          right: 16,
          zIndex: 3000,
          display: isDrawerOpen ? "none" : "",
          "&:hover": {
            backgroundColor: DEFAULT_COLOR,
          },
        }}
        onClick={() => window.open(BOT_URL, "_blank")}
      >
        <Bot />
      </Fab>
      <UserDrawer
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />
    </Grid>
  );
}
